import { context, propagation } from '@opentelemetry/api';
import { getBaggageEntries } from '../baggage/baggageRegistry';
import { getAgencyApiHost, getApiHost } from '../../api/host-by-env';

type ShouldAttachFn = (url: string) => boolean;

function toOriginPattern(url: string): RegExp {
  const { origin } = new URL(url);
  const escaped = origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`^${escaped}(?=$|[/?#])`);
}

function buildCorsAllowlist(): RegExp[] {
  try {
    return [toOriginPattern(getApiHost()), toOriginPattern(getAgencyApiHost())];
  } catch {
    return [];
  }
}

const PROPAGATE_TRACE_HEADER_CORS_URLS: RegExp[] = buildCorsAllowlist();

/**
 * Install fetch wrapper that attaches baggage to allowlisted cross-origin requests.
 * Must run after FetchInstrumentation has patched window.fetch.
 */
export function installAmbientBaggageFetch(shouldAttach: ShouldAttachFn): void {
  const patchedFetch = window.fetch;

  window.fetch = function (this: unknown, input: RequestInfo | URL, init?: RequestInit) {
    try {
      const rawUrl = typeof input === 'string' ? input : (input as Request | URL).toString();
      const url = (() => {
        try {
          return new URL(rawUrl, location.href).toString();
        } catch {
          return rawUrl;
        }
      })();
      if (!shouldAttach(url)) return patchedFetch.call(this, input, init);

      const entries = getBaggageEntries();
      const keys = Object.keys(entries);
      if (keys.length === 0) return patchedFetch.call(this, input, init);

      const baggage = propagation.createBaggage(Object.fromEntries(keys.map((k) => [k, { value: entries[k] }])));
      const ctx = propagation.setBaggage(context.active(), baggage);
      return context.with(ctx, () => patchedFetch.call(this, input, init));
    } catch {
      return patchedFetch.call(this, input, init);
    }
  };
}

export function bootstrapPropagation(): void {
  installAmbientBaggageFetch((url) => PROPAGATE_TRACE_HEADER_CORS_URLS.some((pattern) => pattern.test(url)));
}
